import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { LogIn, Building2, Eye, EyeOff, ArrowLeft, Mail, Loader2, AlertCircle } from 'lucide-react';

type LoginStep = 'email' | 'password';

export default function Login() {
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { discoverTenant, login, discoveredTenant, clearDiscoveredTenant, resolvedBranding } = useAuth();
  const navigate = useNavigate();

  // Dynamic branding from custom domain resolution or discovered tenant
  const brandColor = resolvedBranding?.primaryColor || discoveredTenant?.primaryColor || 'var(--dg-brand)';
  const brandName = resolvedBranding?.companyName || resolvedBranding?.tenantName || 'Müşteri Portalı';
  const brandLogo = resolvedBranding?.logoUrl || discoveredTenant?.logoUrl;

  // Detect custom domain
  const hostname = window.location.hostname;
  const isCustomDomain = !['destekgor.com', 'localhost'].some(
    d => hostname === d || hostname.endsWith(`.${d}`)
  );

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await discoverTenant(email);
      if (result) {
        setStep('password');
      } else {
        setError('Bu e-posta adresi ile kayıtlı bir hesap bulunamadı');
      }
    } catch {
      setError('Bir hata oluştu, lütfen tekrar deneyin');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Şifre hatalı');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep('email');
    setPassword('');
    setError('');
    if (!isCustomDomain) {
      clearDiscoveredTenant();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo / Title */}
        <div className="text-center mb-8">
          {brandLogo ? (
            <img src={brandLogo} alt={brandName} className="w-16 h-16 rounded-2xl mx-auto mb-4 object-cover shadow-lg" />
          ) : (
            <div
              className="inline-flex items-center justify-center w-16 h-16 text-white rounded-2xl mb-4 shadow-lg"
              style={{ backgroundColor: brandColor }}
            >
              <Building2 className="w-8 h-8" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900">{brandName}</h1>
          <p className="text-gray-600 mt-2">
            {step === 'email'
              ? 'Hesabınıza giriş yapmak için e-posta adresinizi girin'
              : 'Şifrenizi girerek devam edin'
            }
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Step 1: Email Discovery */}
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-5">
              {error && (
                <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  E-posta Adresi
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ornek@sirket.com"
                    required
                    autoFocus
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg transition-colors"
                    style={{ '--tw-ring-color': brandColor } as React.CSSProperties}
                    onFocus={(e) => { e.target.style.borderColor = brandColor; e.target.style.boxShadow = `0 0 0 2px ${brandColor}30`; }}
                    onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || !email}
                className="w-full py-3 px-4 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: brandColor }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Kontrol ediliyor...
                  </>
                ) : (
                  'Devam Et'
                )}
              </button>
            </form>
          )}

          {/* Step 2: Password Entry */}
          {step === 'password' && discoveredTenant && (
            <form onSubmit={handlePasswordSubmit} className="space-y-5">
              {/* Back Button */}
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors -mt-2 mb-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Geri
              </button>

              {/* Tenant Info */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <div
                  className="flex w-10 h-10 items-center justify-center rounded-lg"
                  style={{ backgroundColor: `${brandColor}15` }}
                >
                  {(discoveredTenant.logoUrl || brandLogo) ? (
                    <img
                      src={discoveredTenant.logoUrl || brandLogo}
                      alt={discoveredTenant.tenantName}
                      className="w-8 h-8 rounded object-cover"
                    />
                  ) : (
                    <Building2 className="w-5 h-5" style={{ color: brandColor }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{discoveredTenant.tenantName}</p>
                  <p className="text-sm text-gray-500 truncate">{email}</p>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Parola
                  </label>
                  <a
                    href="/forgot-password"
                    className="text-sm hover:opacity-80"
                    style={{ color: brandColor }}
                  >
                    Parolamı unuttum
                  </a>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg transition-colors pr-10"
                    onFocus={(e) => { e.target.style.borderColor = brandColor; e.target.style.boxShadow = `0 0 0 2px ${brandColor}30`; }}
                    onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || !password}
                className="w-full py-3 px-4 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: brandColor }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Giriş yapılıyor...
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    Giriş Yap
                  </>
                )}
              </button>
            </form>
          )}

        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Yardım için destek ekibiyle iletişime geçin
        </p>

        {isCustomDomain && (
          <p className="mt-4 text-center text-xs text-gray-400">
            Powered by{' '}
            <a href="https://destekgor.com" target="_blank" rel="noopener noreferrer" className="font-medium text-gray-500 hover:text-gray-600">
              DestekGör
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
